import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { PriceType, PricingGrpcService, PricingItem, streamConnectionState } from '@pinnakl/grpc/pricing';
import { EVENT_STREAM_BUFFER_TIMEOUT } from '@pinnakl/pms/domain';
import { PriceEventData } from '@pinnakl/poems/streams/price/domain';
import { withDevtools } from '@pinnakl/shared/signal-store';
import { bufferTime, filter, map, Observable, Subscription } from 'rxjs';

interface PriceEventsStreamState {
  eventsBySecurityMarketId: Record<string, PriceEventData>;
  loading: boolean;
  error: string | null;
  connected: boolean;
  connectionError: boolean;
  timestamp: number | null;
}

const initialState: PriceEventsStreamState = {
  eventsBySecurityMarketId: {},
  loading: false,
  error: null,
  connected: false,
  connectionError: false,
  timestamp: null,
};

const priceItemsToPriceEvents = (items: PricingItem[]): PriceEventData[] =>
  items.flatMap(item =>
    item.pricetype === PriceType.Mid && item.value != null
      ? [
          {
            clientId: item.clientid,
            value: item.value,
            securityMarketId: item.securitymarketid,
            priceType: 'mid',
            currency: item.currency,
            source: item.source,
            evalTime: new Date(item.evaltime * 1000).toDateString(),
          },
        ]
      : [],
  );

export const PriceEventsStreamStore = signalStore(
  { providedIn: 'root' },
  withDevtools('priceEventsStream'),
  withState(initialState),
  withProps(store => ({
    latestPriceEventsDictionary$: toObservable(store.eventsBySecurityMarketId),
  })),
  withMethods((store, pricingGrpc = inject(PricingGrpcService)) => {
    let streamSubscription: Subscription | null = null;

    return {
      subscribeToStream(): void {
        if (streamSubscription) return;
        patchState(store, { loading: true });
        streamSubscription = new Subscription();
        streamSubscription.add(
          pricingGrpc.status$.subscribe(status => patchState(store, streamConnectionState(status))),
        );
        streamSubscription.add(
          pricingGrpc.items$
            .pipe(
              bufferTime(EVENT_STREAM_BUFFER_TIMEOUT),
              filter(items => items.length > 0),
            )
            .subscribe({
              next: items => {
                const events = Object.fromEntries(
                  priceItemsToPriceEvents(items).map(event => [event.securityMarketId.toString(), event]),
                );
                patchState(store, {
                  eventsBySecurityMarketId: { ...store.eventsBySecurityMarketId(), ...events },
                  loading: false,
                  timestamp: Date.now(),
                });
              },
              error: error => patchState(store, { error: String(error), loading: false }),
            }),
        );
      },
      getPriceEventsBySecurityMarketId(securityMarketId: string | number): Observable<PriceEventData | undefined> {
        return store.latestPriceEventsDictionary$.pipe(map(events => events[securityMarketId.toString()]));
      },
      unsubscribeFromStream(): void {
        streamSubscription?.unsubscribe();
        streamSubscription = null;
      },
    };
  }),
);
